import {
  MouseEvent,
  ReactNode,
  Ref,
  UIEvent,
  useState,
} from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Drawer,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ArrowDropDown, Check, Search } from "@mui/icons-material";

import CustomScrollbars from "components/CustomScrollbars";
import { STRONG_DROPDOWN_PAPER_SX } from "components/common/dropdownPaper";

export type ClientSwitcherUser = { id: number; label: string };

export interface ClientSwitcherListProps {
  users: ClientSwitcherUser[];
  /** Shown above the full list while the search is empty. */
  recent?: ClientSwitcherUser[];
  selectedId: number | null;
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  sortControl?: ReactNode;
  onSelect: (id: number) => void;
  onScroll?: (event: UIEvent<HTMLElement>) => void;
  scrollRef?: Ref<HTMLDivElement | HTMLUListElement>;
  autoFocus?: boolean;
}

const SECTION_LABEL_SX = {
  px: 2,
  pt: 1,
  pb: 0.5,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  color: "text.secondary",
} as const;

function UserRow({
  user,
  selected,
  onSelect,
}: {
  user: ClientSwitcherUser;
  selected: boolean;
  onSelect: (id: number) => void;
}) {
  return (
    <Box component="li" sx={{ listStyle: "none" }}>
      <ButtonBase
        onClick={() => onSelect(user.id)}
        aria-current={selected ? "true" : undefined}
        title={user.label}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.25,
          width: "100%",
          minHeight: 44,
          px: 2,
          textAlign: "left",
          fontSize: 16,
          fontWeight: 700,
          color: "text.primary",
          bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : "transparent",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, selected ? 0.12 : 0.05) },
          "&.Mui-focusVisible": { bgcolor: alpha(theme.palette.primary.main, 0.12) },
        })}
      >
        <Box
          component="span"
          sx={{ width: 20, flexShrink: 0, display: "inline-flex", color: "primary.main" }}
        >
          {selected ? <Check sx={{ fontSize: 20 }} /> : null}
        </Box>
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {user.label}
        </Box>
      </ButtonBase>
    </Box>
  );
}

/** Search, sort and the user list - in the switcher's dropdown, its phone sheet and the empty page. */
export function ClientSwitcherList({
  users,
  recent = [],
  selectedId,
  loading,
  search,
  onSearchChange,
  sortControl,
  onSelect,
  onScroll,
  scrollRef,
  autoFocus = false,
}: ClientSwitcherListProps) {
  const showRecent = !search.trim() && recent.length > 0;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}>
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1.5,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <TextField
          size="small"
          fullWidth
          autoFocus={autoFocus}
          placeholder="Search users..."
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          inputProps={{ "aria-label": "Search users" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        {sortControl}
      </Box>
      <CustomScrollbars
        ref={scrollRef}
        sx={{ flex: 1, minHeight: 0 }}
        preserveScrollOnGrow
        scrollableProps={{ onScroll }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box component="ul" aria-label="Users" sx={{ m: 0, p: 0, py: 0.75 }}>
            {showRecent && (
              <>
                <Box component="li" sx={{ ...SECTION_LABEL_SX, listStyle: "none" }}>
                  Recent
                </Box>
                {recent.map((user) => (
                  <UserRow
                    key={`recent-${user.id}`}
                    user={user}
                    selected={user.id === selectedId}
                    onSelect={onSelect}
                  />
                ))}
                <Box
                  component="li"
                  sx={{
                    ...SECTION_LABEL_SX,
                    listStyle: "none",
                    mt: 0.75,
                    pt: 1.25,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  All users
                </Box>
              </>
            )}
            {users.map((user) => (
              <UserRow
                key={user.id}
                user={user}
                selected={user.id === selectedId}
                onSelect={onSelect}
              />
            ))}
            {!users.length && (
              <Box component="li" sx={{ listStyle: "none", px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  No users found.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CustomScrollbars>
    </Box>
  );
}

export interface ClientSwitcherProps extends Omit<ClientSwitcherListProps, "autoFocus"> {
  /** Name on the button; null before a user is picked. */
  label: string | null;
  /** Phone: the list opens as a bottom sheet instead of a dropdown. */
  sheet?: boolean;
}

/** The first breadcrumb of every Automations screen: the user being worked on, and the way to another. */
export default function ClientSwitcher({ label, sheet = false, onSelect, ...listProps }: ClientSwitcherProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);
  const pick = (id: number) => {
    close();
    onSelect(id);
  };
  const shown = label ?? "Select a user";
  const list = <ClientSwitcherList {...listProps} onSelect={pick} autoFocus={!sheet} />;
  return (
    <>
      <ButtonBase
        aria-haspopup="listbox"
        aria-expanded={open}
        title={label ? `Switch user: ${label}` : "Select a user"}
        onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
        sx={(theme) => ({
          flex: "0 1 auto",
          minWidth: 0,
          maxWidth: sheet ? "100%" : 280,
          height: 36,
          ml: "-6px",
          pl: "6px",
          borderRadius: "6px",
          display: "inline-flex",
          alignItems: "center",
          fontSize: 16,
          fontWeight: 600,
          color: "text.primary",
          bgcolor: open ? alpha(theme.palette.primary.main, 0.1) : "transparent",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}` },
        })}
      >
        <Box
          component="span"
          sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {shown}
        </Box>
        <ArrowDropDown
          sx={{
            flexShrink: 0,
            ml: "-3px",
            color: "action.active",
            transition: "transform 150ms",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      </ButtonBase>
      {sheet ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              height: "min(88vh, 560px)",
              "@supports (height: 100dvh)": { height: "min(88dvh, 560px)" },
              pb: "env(safe-area-inset-bottom, 0px)",
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ px: 2, pt: 2, flexShrink: 0 }}>
            Backend users
          </Typography>
          {list}
        </Drawer>
      ) : (
        <Popover
          open={open}
          anchorEl={anchor}
          onClose={close}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: [
                STRONG_DROPDOWN_PAPER_SX,
                {
                  mt: 0.5,
                  width: 400,
                  maxWidth: "calc(100vw - 32px)",
                  maxHeight: "min(640px, calc(100vh - 140px))",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "8px",
                },
              ],
            },
          }}
        >
          {list}
        </Popover>
      )}
    </>
  );
}
